import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, Lock, HeartHandshake } from "lucide-react";
import api from "@/lib/api";
import GoalCard from "@/components/GoalCard";
import ContributeModal from "@/components/ContributeModal";
import VideoSection, { hasVideo } from "@/components/VideoSection";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import { brl } from "@/lib/format";

export default function PublicPage() {
  const navigate = useNavigate();
  const [metas, setMetas] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    api.get("/metas").then((res) => setMetas(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get("/config").then((r) => { setConfig(r.data); document.title = `${r.data.nome_igreja} — ${r.data.titulo_pagina}`; }).catch(() => {});
  }, [load]);

  const abertas = metas.filter((m) => m.status !== "pago");
  const concluidas = metas.length - abertas.length;
  const totalArrecadado = metas.reduce((s, m) => s + m.valor_arrecadado, 0);
  const nome = config?.nome_igreja || "Igreja Unida Deus Proverá";

  return (
    <div className="min-h-screen bg-[#f4f7fb] overflow-x-hidden">
      <header className="w-full bg-white/90 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0" data-testid="church-header-logo">
            <img src="/brand/logo.jpg" alt={nome} data-testid="church-logo-image" className="h-11 sm:h-14 w-auto rounded-xl shadow-[0_6px_20px_-6px_rgba(15,42,107,0.6)] shrink-0 object-cover" />
            <div className="min-w-0">
              <p className="font-heading text-base sm:text-xl font-extrabold text-[#0f2a6b] tracking-tight leading-tight truncate">{nome}</p>
              <p className="text-[11px] sm:text-xs text-slate-500 font-medium tracking-wide">pg.iudp.com.br</p>
            </div>
          </div>
          <button
            data-testid="admin-login-button"
            onClick={() => navigate("/adm")}
            aria-label="Área administrativa"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
          >
            <Lock className="w-3 h-3" /> ADM
          </button>
        </div>
      </header>

      <section className="relative overflow-hidden bg-[#0f2a6b] text-white">
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-blue-500/25 blur-3xl" />
          <div className="absolute -bottom-40 -left-24 w-[24rem] h-[24rem] rounded-full bg-sky-400/15 blur-3xl" />
          <div className="absolute inset-0 hero-grain" />
        </div>
        <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14 sm:pt-16 sm:pb-20 grid grid-cols-1 gap-8 lg:gap-12 items-center ${hasVideo(config) ? "lg:grid-cols-[minmax(0,1fr)_minmax(360px,520px)]" : ""}`}>
          <div>
          <p className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold tracking-wider uppercase text-blue-200/90 fade-up">
            <ShieldCheck className="w-4 h-4" /> Transparência em cada contribuição
          </p>
          <h1 data-testid="panel-title" className={`font-heading mt-4 font-extrabold tracking-tight leading-[1.05] max-w-3xl fade-up ${hasVideo(config) ? "text-4xl sm:text-5xl" : "text-4xl sm:text-5xl lg:text-6xl"}`} style={{ animationDelay: "80ms" }}>
            {config?.titulo_pagina || "Painel de Pagamentos e Contribuições"}
          </h1>
          <p data-testid="panel-intro" className="mt-5 text-base md:text-lg text-blue-100/90 max-w-2xl leading-relaxed fade-up" style={{ animationDelay: "160ms" }}>
            {config?.texto_apresentacao || "Escolha uma conta que deseja nos ajudar a pagar. Cada contribuição é uma bênção."}
          </p>
          {!loading && metas.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-3 sm:gap-4 fade-up" style={{ animationDelay: "240ms" }} data-testid="hero-summary">
              <Stat label="Metas em aberto" value={abertas.length} />
              <Stat label="Metas concluídas" value={concluidas} />
              <Stat label="Já arrecadado" value={brl(totalArrecadado)} />
            </div>
          )}
          </div>
          <VideoSection config={config} />
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 relative -mt-6 sm:-mt-8">
        {loading ? (
          <div className="flex justify-center py-24" data-testid="goals-loading">
            <Loader2 className="w-10 h-10 animate-spin text-[#0f2a6b]" />
          </div>
        ) : metas.length === 0 ? (
          <div data-testid="goals-empty" className="bg-white rounded-3xl border border-slate-200 p-12 text-center shadow-sm">
            <HeartHandshake className="w-12 h-12 mx-auto text-blue-300" />
            <p className="mt-4 text-lg text-slate-600">Nenhuma meta disponível no momento. Volte em breve.</p>
          </div>
        ) : (
          <div data-testid="goals-grid-container" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-7">
            {metas.map((meta, i) => (
              <GoalCard key={meta.id} meta={meta} index={i} onHelp={() => setSelected(meta)} />
            ))}
          </div>
        )}
      </main>

      <TestimonialsCarousel enabled={!!config?.testemunhos_ativo} />

      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <p className="font-medium">{nome} · pg.iudp.com.br</p>
          <p className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Cada pagamento é conferido pela administração.</p>
        </div>
      </footer>

      {selected && (
        <ContributeModal meta={selected} config={config} open={!!selected} onClose={() => setSelected(null)} onSubmitted={load} />
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl px-4 py-3 min-w-[9rem]">
      <p className="font-heading text-xl sm:text-2xl font-extrabold leading-tight">{value}</p>
      <p className="text-xs sm:text-sm text-blue-100/80 font-medium">{label}</p>
    </div>
  );
}
